<script setup lang="ts">
  import { computed, ref, watch } from 'vue';
  import { useRoute } from 'vue-router/composables';
  import {
    activeNavKey,
    INavGroup,
    isNavSectionOpen,
    navBadge,
  } from '@/domain/layout/app-nav';
  import V2ShellNavItem from './V2ShellNavItem.vue';

  /**
   * The drawer's navigation (plan §4.2): plain links, groups that expand
   * (open while the route is inside them) and page parents whose sub-pages
   * show while the route is inside their section.
   */
  const props = defineProps<{
    groups: INavGroup[];
    effectiveTier: number;
    /** Accessible name of the `<nav>`. */
    label: string;
  }>();

  const route = useRoute();
  const path = computed(() => route.path);

  /** Groups the user opened or closed by hand. */
  const toggled = ref<Record<string, boolean>>({});

  const activeKey = computed(() => activeNavKey(path.value, props.groups));

  function isSectionOpen(group: INavGroup): boolean {
    return isNavSectionOpen(path.value, group);
  }

  function isExpanded(group: INavGroup): boolean {
    return toggled.value[group.key] ?? isSectionOpen(group);
  }

  function toggle(group: INavGroup) {
    toggled.value = { ...toggled.value, [group.key]: !isExpanded(group) };
  }

  /** Children rendered under a group: always for expanded groups, in-section for pages. */
  function showChildren(group: INavGroup): boolean {
    if (!group.children?.length) return false;
    return group.to ? isSectionOpen(group) : isExpanded(group);
  }

  // Navigating into a group opens it again, even if it was closed by hand.
  watch(path, () => {
    const next = { ...toggled.value };
    for (const group of props.groups)
      if (isSectionOpen(group)) delete next[group.key];
    toggled.value = next;
  });
</script>

<template>
  <nav class="v2-shell-nav" :aria-label="label">
    <ul class="nav-list">
      <li v-for="group in groups" :key="group.key" class="nav-group">
        <V2ShellNavItem
          :title-key="group.titleKey"
          :to="group.to"
          :icon="group.icon"
          :active="activeKey === group.key"
          :badge="navBadge(group, effectiveTier)"
          :expandable="!group.to"
          :expanded="isExpanded(group)"
          @toggle="toggle(group)"
        />
        <ul v-if="showChildren(group)" class="nav-children">
          <li v-for="child in group.children" :key="child.key">
            <V2ShellNavItem
              :title-key="child.titleKey"
              :to="child.to"
              :level="1"
              :active="activeKey === child.key"
              :badge="navBadge(child, effectiveTier)"
            />
          </li>
        </ul>
      </li>
    </ul>
  </nav>
</template>

<style scoped lang="scss">
  .v2-shell-nav {
    @apply tw-py-2;

    .nav-list,
    .nav-children {
      @apply tw-m-0 tw-list-none tw-p-0;
    }
  }
</style>
